import { boxShadow } from "@somethings-ui/core";
import { UserAvatar } from "@/components/ui/stacked-avatars";
import type {
  ActivityStatus,
  ConnectActivityResult,
} from "@/lib/api/activities";
import { countConnectLevelsCompleted } from "@/lib/activities/connect-shapes";
import { cn } from "@/lib/utils";
import { Mic } from "lucide-react";
import { FireIcon } from "./fire-icon";
import { PeriwinkleCard } from "./periwinkle-card";

interface ConnectMomentChatCardProps {
  activityStatus: ActivityStatus;
  isExpired: boolean;
  result: ConnectActivityResult | null;
  teenName: string;
  ownerAvatarUrl: string | null;
  teenAvatarUrl: string | null;
  onOpen: () => void;
}

export function ConnectMomentChatCard({
  activityStatus,
  isExpired,
  result,
  teenName,
  ownerAvatarUrl,
  teenAvatarUrl,
  onOpen,
}: ConnectMomentChatCardProps) {
  if (isExpired) {
    return (
      <div className="flex items-center gap-2 self-center rounded-xl bg-zinc-50 px-4 py-2 text-sm text-zinc-500">
        <Mic className="h-4 w-4" />
        Connection cards expired
      </div>
    );
  }

  const levelsCompleted = countConnectLevelsCompleted(result);
  const totalLevels = 3;

  const counterLabel =
    activityStatus === "completed"
      ? `${totalLevels}/${totalLevels} levels`
      : `${levelsCompleted}/${totalLevels} levels`;

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open Connection cards from your conversation with ${teenName}`}
      className={cn(
        "self-center text-left",
        "rounded-2xl transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-periwinkle/40 focus:ring-offset-2",
      )}
      style={{ boxShadow: boxShadow.small }}
    >
      <PeriwinkleCard
        variant="highlight"
        className="w-[324px] max-w-full rounded-2xl p-4"
      >
        <div className="flex flex-col gap-4">
          {/* Stacked avatars top-left, mentor under teen */}
          <div className="flex items-center">
            <div className="-mr-[10px]">
              <UserAvatar
                imageUrl={ownerAvatarUrl ?? undefined}
                name="Mentor"
                size={32}
                showBorder
              />
            </div>
            <div>
              <UserAvatar
                imageUrl={teenAvatarUrl ?? undefined}
                name={teenName}
                size={32}
                showBorder
              />
            </div>
          </div>

          {/* Title row */}
          <div className="flex items-center gap-2">
            <span className="flex-1 text-xl font-bold tracking-tight text-white">
              Connection cards
            </span>
            <span className="font-mono text-xs tabular-nums text-white">
              {counterLabel}
            </span>
          </div>

          {/* Fire emoji row — one per level, dim the unfinished ones */}
          <div className="flex items-center gap-3">
            {Array.from({ length: totalLevels }).map((_, i) => (
              <FireBadge key={i} lit={i < levelsCompleted} />
            ))}
          </div>
        </div>
      </PeriwinkleCard>
    </button>
  );
}

function FireBadge({ lit }: { lit: boolean }) {
  return <FireIcon size={32} opacity={lit ? 1 : 0.35} />;
}
